import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  FaSave,
  FaUndo,
  FaPlus,
  FaTrash,
  FaEdit,
  FaTimes,
  FaFilePdf,
  FaFileWord,
  FaSearch,
  FaFolderPlus,
  FaArrowLeft,
  FaExternalLinkAlt,
  FaUpload,
  FaCheckCircle,
  FaArrowUp,
  FaArrowDown,
  FaEye,
} from 'react-icons/fa';
import {
  LuFileSpreadsheet,
  LuFolderKanban,
  LuLayoutDashboard,
  LuLayers,
  LuListChecks,
  LuInfo,
  LuSparkles,
} from 'react-icons/lu';
import { useToast } from '../../../context/ToastContext';
import PageLayout from '../../../components/ui/PageLayout';
import PageHeader from '../../../components/ui/PageHeader';
import TabBar from '../../../components/ui/TabBar';
import SkeletonLoader from '../../../components/ui/SkeletonLoader';

const SLUG = 'business-contract-templates';

const defaultForm = () => ({
  isPublished: true,
  pageTitle: 'Free Business Legal Templates',
  pageSubtitle:
    'Access a collection of commonly used business and legal document templates—available free for BooksComply clients.',
  introParagraph:
    'These templates can be downloaded directly to your computer and customized to suit your business requirements. They are designed to provide a useful starting point for common business transactions, relationships, and corporate activities.',
  importantNotice:
    'Important: These templates are provided for general informational and reference purposes only. They are not prepared specifically for your business or individual circumstances and should not be considered legal advice. Before using any template for an important or legally significant matter, we recommend reviewing and customizing it to reflect your specific situation and, where appropriate, having the final document reviewed by a qualified attorney.',
  categories: [],
  bottomSection: {
    title: 'A Practical Starting Point for Your Business',
    intro:
      'These free templates are intended to help BooksComply clients get started with commonly used business documentation.',
    guidanceTitle:
      'Before signing, sending, or relying on any document for a significant business or legal matter:',
    checklist: [
      'Customize the template to your specific circumstances.',
      'Confirm that the provisions are appropriate for your business and jurisdiction.',
      'Review applicable federal, state, and local requirements.',
      'Consider having the final document reviewed by a qualified attorney when appropriate.',
    ],
    footerNotice:
      'BooksComply provides these templates as a convenience to its clients and does not provide legal advice through these templates.',
  },
});

const ContractTemplatesPortalCMS = () => {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState('templates');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [form, setForm] = useState(defaultForm());

  // Search & Filter
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals state
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null); // null = new, object = edit
  const [templateForm, setTemplateForm] = useState({
    id: '',
    title: '',
    category: '',
    newCategoryName: '',
    description: '',
    note: '',
    pdfUrl: '',
    pdfName: '',
    docUrl: '',
    docName: '',
  });

  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategoryIndex, setEditingCategoryIndex] = useState(null); // null = add, number = rename
  const [categoryNameInput, setCategoryNameInput] = useState('');

  // Uploading state
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingRowId, setUploadingRowId] = useState(null);

  const pdfInputRef = useRef(null);
  const docInputRef = useRef(null);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.data.success) {
        const c = res.data.data.content || {};
        setForm({
          ...defaultForm(),
          ...c,
          isPublished: res.data.data.isPublished !== false,
          categories: Array.isArray(c.categories) ? c.categories : [],
          bottomSection: {
            ...defaultForm().bottomSection,
            ...(c.bottomSection || {}),
            checklist: Array.isArray(c.bottomSection?.checklist)
              ? c.bottomSection.checklist
              : defaultForm().bottomSection.checklist,
          },
        });
      }
    } catch {
      toastError('Failed to load contract templates content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  useEffect(() => {
    if (templateModalOpen || categoryModalOpen) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
          setTemplateModalOpen(false);
          setCategoryModalOpen(false);
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = prevOverflow;
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [templateModalOpen, categoryModalOpen]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}`,
        { content: form, isPublished: form.isPublished },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Contract Templates saved & published successfully');
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (
      !window.confirm(
        'Restore all 27 preloaded contract templates and default content? Any custom modifications will be replaced.'
      )
    ) {
      return;
    }
    setResetting(true);
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/admin/${SLUG}/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastSuccess('Default contract templates restored');
      await fetchContent();
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  // Direct file upload handler
  const handleUploadFile = async (file, format) => {
    if (!file) return null;
    const token = localStorage.getItem('adminToken');
    const data = new FormData();
    data.append('file', file);

    const res = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/api/portal-cms/upload`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    if (res.data.success) {
      return res.data.data;
    }
    throw new Error(res.data.message || 'Upload failed');
  };

  // Upload document for a specific template row directly from the list
  const handleDirectRowUpload = async (catIndex, tmplIndex, file, format) => {
    if (!file) return;
    const templateId = form.categories[catIndex].templates[tmplIndex].id;
    setUploadingRowId(`${templateId}-${format}`);
    try {
      const uploaded = await handleUploadFile(file, format);
      setForm((prev) => {
        const nextCategories = [...prev.categories];
        const nextTemplates = [...nextCategories[catIndex].templates];
        const currentTmpl = { ...nextTemplates[tmplIndex] };

        if (format === 'pdf') {
          currentTmpl.pdfUrl = uploaded.fileUrl;
          currentTmpl.pdfName = uploaded.originalName;
        } else {
          currentTmpl.docUrl = uploaded.fileUrl;
          currentTmpl.docName = uploaded.originalName;
        }
        nextTemplates[tmplIndex] = currentTmpl;
        nextCategories[catIndex].templates = nextTemplates;
        return { ...prev, categories: nextCategories };
      });
      toastSuccess(`${format.toUpperCase()} document uploaded. Click Save Changes to publish.`);
    } catch (err) {
      toastError(err.message || 'File upload failed');
    } finally {
      setUploadingRowId(null);
    }
  };

  // Remove document from a template
  const handleRemoveDocument = (catIndex, tmplIndex, format) => {
    if (!window.confirm(`Remove ${format.toUpperCase()} file from this template?`)) return;
    setForm((prev) => {
      const nextCategories = [...prev.categories];
      const nextTemplates = [...nextCategories[catIndex].templates];
      const currentTmpl = { ...nextTemplates[tmplIndex] };

      if (format === 'pdf') {
        currentTmpl.pdfUrl = '';
        currentTmpl.pdfName = '';
      } else {
        currentTmpl.docUrl = '';
        currentTmpl.docName = '';
      }
      nextTemplates[tmplIndex] = currentTmpl;
      nextCategories[catIndex].templates = nextTemplates;
      return { ...prev, categories: nextCategories };
    });
    toastSuccess(`${format.toUpperCase()} file removed. Remember to Save Changes.`);
  };

  // Open Template Modal for Adding
  const handleOpenAddTemplate = (defaultCatName = '') => {
    const existingCat = defaultCatName || form.categories[0]?.category || '';
    setEditingTemplate(null);
    setTemplateForm({
      id: `tmpl_${Date.now()}`,
      title: '',
      category: existingCat,
      newCategoryName: '',
      description: '',
      note: '',
      pdfUrl: '',
      pdfName: '',
      docUrl: '',
      docName: '',
    });
    setTemplateModalOpen(true);
  };

  // Open Template Modal for Editing
  const handleOpenEditTemplate = (catIndex, tmplIndex) => {
    const cat = form.categories[catIndex];
    const tmpl = cat.templates[tmplIndex];
    setEditingTemplate({ catIndex, tmplIndex });
    setTemplateForm({
      id: tmpl.id || `tmpl_${Date.now()}`,
      title: tmpl.title || '',
      category: cat.category,
      newCategoryName: '',
      description: tmpl.description || '',
      note: tmpl.note || '',
      pdfUrl: tmpl.pdfUrl || '',
      pdfName: tmpl.pdfName || '',
      docUrl: tmpl.docUrl || '',
      docName: tmpl.docName || '',
    });
    setTemplateModalOpen(true);
  };

  // Save Template from Modal
  const handleSaveTemplateModal = () => {
    if (!templateForm.title.trim()) {
      toastError('Please enter a template title');
      return;
    }
    const finalCategory =
      templateForm.category === '__NEW__'
        ? templateForm.newCategoryName.trim()
        : templateForm.category.trim();

    if (!finalCategory) {
      toastError('Please choose or enter a category name');
      return;
    }

    setForm((prev) => {
      const nextCategories = [...prev.categories];

      // Remove from previous category if editing and category changed
      if (editingTemplate) {
        const { catIndex, tmplIndex } = editingTemplate;
        const oldCat = nextCategories[catIndex];
        if (oldCat.category === finalCategory) {
          // Same category, just update
          oldCat.templates[tmplIndex] = {
            id: templateForm.id,
            title: templateForm.title.trim(),
            description: templateForm.description.trim(),
            note: templateForm.note.trim() || undefined,
            pdfUrl: templateForm.pdfUrl,
            pdfName: templateForm.pdfName,
            docUrl: templateForm.docUrl,
            docName: templateForm.docName,
          };
          return { ...prev, categories: nextCategories };
        } else {
          // Category changed: remove from old
          oldCat.templates.splice(tmplIndex, 1);
        }
      }

      // Add to target category
      let targetCat = nextCategories.find((c) => c.category === finalCategory);
      if (!targetCat) {
        targetCat = { category: finalCategory, templates: [] };
        nextCategories.push(targetCat);
      }

      targetCat.templates.push({
        id: templateForm.id || `tmpl_${Date.now()}`,
        title: templateForm.title.trim(),
        description: templateForm.description.trim(),
        note: templateForm.note.trim() || undefined,
        pdfUrl: templateForm.pdfUrl,
        pdfName: templateForm.pdfName,
        docUrl: templateForm.docUrl,
        docName: templateForm.docName,
      });

      return { ...prev, categories: nextCategories };
    });

    setTemplateModalOpen(false);
    toastSuccess(
      editingTemplate
        ? 'Template updated! Click Save Changes to publish.'
        : 'Template added! Click Save Changes to publish.'
    );
  };

  // Delete Template
  const handleDeleteTemplate = (catIndex, tmplIndex) => {
    const tmplTitle = form.categories[catIndex]?.templates[tmplIndex]?.title;
    if (!window.confirm(`Are you sure you want to delete "${tmplTitle}"?`)) return;

    setForm((prev) => {
      const nextCategories = [...prev.categories];
      nextCategories[catIndex].templates.splice(tmplIndex, 1);
      return { ...prev, categories: nextCategories };
    });
    toastSuccess(`"${tmplTitle}" deleted. Remember to Save Changes.`);
  };

  // Category management
  const handleOpenAddCategory = () => {
    setEditingCategoryIndex(null);
    setCategoryNameInput('');
    setCategoryModalOpen(true);
  };

  const handleOpenRenameCategory = (catIndex) => {
    setEditingCategoryIndex(catIndex);
    setCategoryNameInput(form.categories[catIndex].category);
    setCategoryModalOpen(true);
  };

  const handleSaveCategoryModal = () => {
    const name = categoryNameInput.trim();
    if (!name) {
      toastError('Please enter a category name');
      return;
    }

    setForm((prev) => {
      const nextCategories = [...prev.categories];
      if (editingCategoryIndex !== null) {
        nextCategories[editingCategoryIndex].category = name;
      } else {
        if (nextCategories.some((c) => c.category.toLowerCase() === name.toLowerCase())) {
          toastError('A category with this name already exists');
          return prev;
        }
        nextCategories.push({ category: name, templates: [] });
      }
      return { ...prev, categories: nextCategories };
    });

    setCategoryModalOpen(false);
    toastSuccess(
      editingCategoryIndex !== null
        ? 'Category renamed! Click Save Changes to publish.'
        : 'Category created! Click Save Changes to publish.'
    );
  };

  const handleDeleteCategory = (catIndex) => {
    const cat = form.categories[catIndex];
    const count = cat?.templates?.length || 0;
    const msg =
      count > 0
        ? `Are you sure you want to delete category "${cat.category}" and its ${count} templates?`
        : `Delete empty category "${cat.category}"?`;

    if (!window.confirm(msg)) return;

    setForm((prev) => {
      const nextCategories = [...prev.categories];
      nextCategories.splice(catIndex, 1);
      return { ...prev, categories: nextCategories };
    });
    toastSuccess(`Category "${cat.category}" removed.`);
  };

  // Checklist items in bottom section
  const handleAddChecklistItem = () => {
    setForm((prev) => ({
      ...prev,
      bottomSection: {
        ...prev.bottomSection,
        checklist: [...(prev.bottomSection?.checklist || []), 'New guidance point'],
      },
    }));
  };

  const handleUpdateChecklistItem = (idx, val) => {
    setForm((prev) => {
      const list = [...(prev.bottomSection?.checklist || [])];
      list[idx] = val;
      return {
        ...prev,
        bottomSection: { ...prev.bottomSection, checklist: list },
      };
    });
  };

  const handleDeleteChecklistItem = (idx) => {
    setForm((prev) => {
      const list = [...(prev.bottomSection?.checklist || [])];
      list.splice(idx, 1);
      return {
        ...prev,
        bottomSection: { ...prev.bottomSection, checklist: list },
      };
    });
  };

  const handleMoveChecklistItem = (idx, direction) => {
    const list = [...(form.bottomSection?.checklist || [])];
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= list.length) return;
    const temp = list[idx];
    list[idx] = list[newIdx];
    list[newIdx] = temp;
    setForm((prev) => ({
      ...prev,
      bottomSection: { ...prev.bottomSection, checklist: list },
    }));
  };

  // Stats calculation
  const totalCategories = form.categories?.length || 0;
  const totalTemplates =
    form.categories?.reduce((sum, c) => sum + (c.templates?.length || 0), 0) || 0;
  const totalPdfsUploaded =
    form.categories?.reduce(
      (sum, c) => sum + (c.templates?.filter((t) => Boolean(t.pdfUrl)).length || 0),
      0
    ) || 0;
  const totalDocsUploaded =
    form.categories?.reduce(
      (sum, c) => sum + (c.templates?.filter((t) => Boolean(t.docUrl)).length || 0),
      0
    ) || 0;

  // Filtered categories for UI
  const filteredCategories = form.categories
    .map((cat, catIndex) => {
      if (selectedCategory !== 'All' && cat.category !== selectedCategory) return null;
      const matchedTemplates = cat.templates
        .map((tmpl, tmplIndex) => ({ tmpl, tmplIndex }))
        .filter(({ tmpl }) => {
          const q = searchQuery.toLowerCase().trim();
          if (!q) return true;
          return (
            tmpl.title.toLowerCase().includes(q) ||
            tmpl.description.toLowerCase().includes(q)
          );
        });

      if (matchedTemplates.length === 0 && searchQuery) return null;

      return {
        category: cat.category,
        catIndex,
        matchedTemplates,
      };
    })
    .filter(Boolean);

  const TABS = [
    {
      id: 'templates',
      label: 'Templates & Uploaded Documents',
      badge: totalTemplates,
      icon: <LuFileSpreadsheet className="w-4 h-4" />,
    },
    {
      id: 'header',
      label: 'Page Header & Notice',
      icon: <LuInfo className="w-4 h-4" />,
    },
    {
      id: 'guidance',
      label: 'Bottom Guidance & Disclaimer (Starting Point Card)',
      icon: <LuListChecks className="w-4 h-4" />,
    },
  ];

  if (loading) {
    return (
      <PageLayout>
        <SkeletonLoader />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="space-y-6">
        {/* Top Header */}
        <PageHeader
          icon={LuFileSpreadsheet}
          iconGradient="from-indigo-600 via-blue-600 to-teal-500"
          title="Business Contract Templates CMS"
          description="Manage legal contract templates, categories, text notices, and upload custom PDF & DOC files for user downloads."
          actions={
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => navigate('/portal-cms')}
                className="px-3.5 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-dark-700 hover:bg-gray-50 dark:hover:bg-dark-700 text-gray-700 dark:text-gray-300 flex items-center gap-2"
              >
                <FaArrowLeft className="w-3.5 h-3.5" /> Back to Portal CMS
              </button>

              <button
                type="button"
                onClick={handleReset}
                disabled={resetting || saving}
                className="px-3.5 py-2 rounded-xl text-sm font-medium border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 flex items-center gap-2"
              >
                <FaUndo className={`w-3.5 h-3.5 ${resetting ? 'animate-spin' : ''}`} />
                {resetting ? 'Restoring...' : 'Restore Defaults'}
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={saving || resetting}
                className="btn-premium px-5 py-2 text-sm font-semibold flex items-center gap-2 shadow-lg"
              >
                <FaSave className={`w-3.5 h-3.5 ${saving ? 'animate-spin' : ''}`} />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          }
        />

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-dark-800 p-4 rounded-2xl border border-gray-100 dark:border-dark-700 shadow-sm flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold">
              <LuLayers className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Categories</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{totalCategories}</p>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-800 p-4 rounded-2xl border border-gray-100 dark:border-dark-700 shadow-sm flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold">
              <LuFileSpreadsheet className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">Total Templates</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">{totalTemplates}</p>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-800 p-4 rounded-2xl border border-gray-100 dark:border-dark-700 shadow-sm flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center font-bold">
              <FaFilePdf className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">PDFs Uploaded</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {totalPdfsUploaded} <span className="text-xs text-gray-400">/ {totalTemplates}</span>
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-dark-800 p-4 rounded-2xl border border-gray-100 dark:border-dark-700 shadow-sm flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold">
              <FaFileWord className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">DOCs Uploaded</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {totalDocsUploaded} <span className="text-xs text-gray-400">/ {totalTemplates}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <TabBar tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />

        {/* ======================================================== */}
        {/* TAB 1: TEMPLATES & UPLOADED DOCUMENTS                    */}
        {/* ======================================================== */}
        {activeTab === 'templates' && (
          <div className="space-y-6">
            {/* Filter & Action Toolbar */}
            <div className="bg-white dark:bg-dark-800 p-4 rounded-2xl border border-gray-100 dark:border-dark-700 shadow-sm flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2.5">
                {/* Category selector */}
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="text-xs sm:text-sm rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="All">All Categories ({totalTemplates})</option>
                  {form.categories.map((c) => (
                    <option key={c.category} value={c.category}>
                      {c.category} ({c.templates.length})
                    </option>
                  ))}
                </select>

                {/* Search Input */}
                <div className="relative w-full sm:w-64">
                  <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                  <input
                    type="text"
                    placeholder="Search templates..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-7 py-2 text-xs sm:text-sm rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <FaTimes className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={handleOpenAddCategory}
                  className="px-3.5 py-2 rounded-xl text-xs sm:text-sm font-semibold border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 flex items-center gap-2"
                >
                  <FaFolderPlus className="w-3.5 h-3.5" /> Add Category
                </button>

                <button
                  type="button"
                  onClick={() => handleOpenAddTemplate(selectedCategory !== 'All' ? selectedCategory : '')}
                  className="btn-premium px-4 py-2 text-xs sm:text-sm font-semibold flex items-center gap-2"
                >
                  <FaPlus className="w-3.5 h-3.5" /> Add Template
                </button>
              </div>
            </div>

            {/* Template Categories & Cards */}
            {filteredCategories.length === 0 ? (
              <div className="bg-white dark:bg-dark-800 p-12 text-center rounded-2xl border border-gray-100 dark:border-dark-700 space-y-3">
                <LuFileSpreadsheet className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto" />
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  No Templates Found
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                  {searchQuery
                    ? `No templates match "${searchQuery}". Try a different keyword.`
                    : 'Get started by creating a new contract template or category.'}
                </p>
                <button
                  onClick={() => handleOpenAddTemplate()}
                  className="btn-premium px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 mt-2"
                >
                  <FaPlus className="w-3.5 h-3.5" /> Add First Template
                </button>
              </div>
            ) : (
              filteredCategories.map(({ category, catIndex, matchedTemplates }) => (
                <div
                  key={category}
                  className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 shadow-card overflow-hidden"
                >
                  {/* Category Header */}
                  <div className="p-4 sm:p-5 bg-gradient-to-r from-gray-50 to-white dark:from-dark-800 dark:to-dark-750 border-b border-gray-100 dark:border-dark-700 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center">
                        <LuFolderKanban className="w-5 h-5" />
                      </div>
                      <div>
                        <h2 className="text-base font-bold text-gray-900 dark:text-white">
                          {category}
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {form.categories[catIndex].templates.length} total templates
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleOpenAddTemplate(category)}
                        title="Add template into this category"
                        className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 hover:bg-primary-100 flex items-center gap-1.5"
                      >
                        <FaPlus className="w-3 h-3" /> Add Template
                      </button>
                      <button
                        type="button"
                        onClick={() => handleOpenRenameCategory(catIndex)}
                        title="Rename category"
                        className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700"
                      >
                        <FaEdit className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteCategory(catIndex)}
                        title="Delete category"
                        className="p-2 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                      >
                        <FaTrash className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Templates List */}
                  <div className="divide-y divide-gray-100 dark:divide-dark-700">
                    {matchedTemplates.map(({ tmpl, tmplIndex }) => {
                      const isUploadingPdfRow =
                        uploadingRowId === `${tmpl.id}-pdf`;
                      const isUploadingDocRow =
                        uploadingRowId === `${tmpl.id}-doc`;

                      return (
                        <div
                          key={tmpl.id || tmplIndex}
                          className="p-4 sm:p-5 hover:bg-gray-50/50 dark:hover:bg-dark-750/50 transition-colors flex flex-col lg:flex-row lg:items-center justify-between gap-4"
                        >
                          {/* Template Info */}
                          <div className="flex-1 space-y-1.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">
                                {tmpl.title}
                              </h3>
                              {tmpl.note && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                                  {tmpl.note}
                                </span>
                              )}
                            </div>
                            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed">
                              {tmpl.description}
                            </p>
                          </div>

                          {/* Uploaded Documents Controls */}
                          <div className="flex flex-wrap items-center gap-3 shrink-0">
                            {/* PDF Document Container */}
                            <div className="p-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50/60 dark:bg-dark-700/60 flex items-center gap-2.5 min-w-[200px]">
                              <FaFilePdf className="w-5 h-5 text-red-500 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                  PDF File
                                </p>
                                {tmpl.pdfUrl ? (
                                  <a
                                    href={`${process.env.REACT_APP_API_BASE_URL || ''}${tmpl.pdfUrl}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline truncate block max-w-[130px]"
                                    title={tmpl.pdfName || 'View PDF'}
                                  >
                                    {tmpl.pdfName || 'Uploaded PDF'}
                                  </a>
                                ) : (
                                  <span className="text-xs text-gray-400 italic">Not uploaded</span>
                                )}
                              </div>

                              {tmpl.pdfUrl ? (
                                <div className="flex items-center gap-1 shrink-0">
                                  <label
                                    title="Replace PDF"
                                    className="p-1.5 rounded-lg text-gray-500 hover:text-gray-800 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-dark-600 cursor-pointer"
                                  >
                                    <FaUpload className="w-3 h-3" />
                                    <input
                                      type="file"
                                      accept=".pdf"
                                      className="hidden"
                                      disabled={isUploadingPdfRow}
                                      onChange={(e) => {
                                        if (e.target.files?.[0]) {
                                          handleDirectRowUpload(
                                            catIndex,
                                            tmplIndex,
                                            e.target.files[0],
                                            'pdf'
                                          );
                                          e.target.value = '';
                                        }
                                      }}
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveDocument(catIndex, tmplIndex, 'pdf')
                                    }
                                    title="Remove PDF"
                                    className="p-1.5 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                                  >
                                    <FaTimes className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <label className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 cursor-pointer flex items-center gap-1">
                                  <FaUpload className="w-2.5 h-2.5" />
                                  {isUploadingPdfRow ? 'Uploading...' : 'Upload'}
                                  <input
                                    type="file"
                                    accept=".pdf"
                                    className="hidden"
                                    disabled={isUploadingPdfRow}
                                    onChange={(e) => {
                                      if (e.target.files?.[0]) {
                                        handleDirectRowUpload(
                                          catIndex,
                                          tmplIndex,
                                          e.target.files[0],
                                          'pdf'
                                        );
                                        e.target.value = '';
                                      }
                                    }}
                                  />
                                </label>
                              )}
                            </div>

                            {/* DOC Document Container */}
                            <div className="p-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50/60 dark:bg-dark-700/60 flex items-center gap-2.5 min-w-[200px]">
                              <FaFileWord className="w-5 h-5 text-blue-600 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                                  Word / DOC
                                </p>
                                {tmpl.docUrl ? (
                                  <a
                                    href={`${process.env.REACT_APP_API_BASE_URL || ''}${tmpl.docUrl}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline truncate block max-w-[130px]"
                                    title={tmpl.docName || 'View DOC'}
                                  >
                                    {tmpl.docName || 'Uploaded DOC'}
                                  </a>
                                ) : (
                                  <span className="text-xs text-gray-400 italic">Not uploaded</span>
                                )}
                              </div>

                              {tmpl.docUrl ? (
                                <div className="flex items-center gap-1 shrink-0">
                                  <label
                                    title="Replace DOC"
                                    className="p-1.5 rounded-lg text-gray-500 hover:text-gray-800 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-dark-600 cursor-pointer"
                                  >
                                    <FaUpload className="w-3 h-3" />
                                    <input
                                      type="file"
                                      accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                      className="hidden"
                                      disabled={isUploadingDocRow}
                                      onChange={(e) => {
                                        if (e.target.files?.[0]) {
                                          handleDirectRowUpload(
                                            catIndex,
                                            tmplIndex,
                                            e.target.files[0],
                                            'doc'
                                          );
                                          e.target.value = '';
                                        }
                                      }}
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveDocument(catIndex, tmplIndex, 'doc')
                                    }
                                    title="Remove DOC"
                                    className="p-1.5 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                                  >
                                    <FaTimes className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <label className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 cursor-pointer flex items-center gap-1">
                                  <FaUpload className="w-2.5 h-2.5" />
                                  {isUploadingDocRow ? 'Uploading...' : 'Upload'}
                                  <input
                                    type="file"
                                    accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                    className="hidden"
                                    disabled={isUploadingDocRow}
                                    onChange={(e) => {
                                      if (e.target.files?.[0]) {
                                        handleDirectRowUpload(
                                          catIndex,
                                          tmplIndex,
                                          e.target.files[0],
                                          'doc'
                                        );
                                        e.target.value = '';
                                      }
                                    }}
                                  />
                                </label>
                              )}
                            </div>

                            {/* Edit / Delete Buttons */}
                            <div className="flex items-center gap-1.5 pl-2 border-l border-gray-100 dark:border-dark-700">
                              <button
                                type="button"
                                onClick={() => handleOpenEditTemplate(catIndex, tmplIndex)}
                                title="Edit Title, Description & Details"
                                className="p-2 rounded-xl text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 border border-primary-100 dark:border-primary-900/30"
                              >
                                <FaEdit className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteTemplate(catIndex, tmplIndex)}
                                title="Delete Template"
                                className="p-2 rounded-xl text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 border border-red-100 dark:border-red-900/30"
                              >
                                <FaTrash className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ======================================================== */}
        {/* TAB 2: PAGE HEADER & NOTICE                             */}
        {/* ======================================================== */}
        {activeTab === 'header' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 shadow-card p-6 space-y-6">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-dark-700">
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  Page Header & Disclaimer Notice
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Configure the primary title, subtitle, introduction text, and legal notice shown at the top of the user page.
                </p>
              </div>

              {/* Publish Toggle */}
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  Published:
                </span>
                <input
                  type="checkbox"
                  checked={form.isPublished}
                  onChange={(e) => setForm((p) => ({ ...p, isPublished: e.target.checked }))}
                  className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500"
                />
              </label>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                  Page Title
                </label>
                <input
                  type="text"
                  value={form.pageTitle || ''}
                  onChange={(e) => setForm((p) => ({ ...p, pageTitle: e.target.value }))}
                  className="w-full text-sm rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3.5 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                  Page Subtitle
                </label>
                <textarea
                  rows={2}
                  value={form.pageSubtitle || ''}
                  onChange={(e) => setForm((p) => ({ ...p, pageSubtitle: e.target.value }))}
                  className="w-full text-sm rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3.5 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                  Intro Paragraph
                </label>
                <textarea
                  rows={3}
                  value={form.introParagraph || ''}
                  onChange={(e) => setForm((p) => ({ ...p, introParagraph: e.target.value }))}
                  className="w-full text-sm rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3.5 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                  Important Notice Box
                </label>
                <textarea
                  rows={4}
                  value={form.importantNotice || ''}
                  onChange={(e) => setForm((p) => ({ ...p, importantNotice: e.target.value }))}
                  className="w-full text-sm rounded-xl border border-amber-300 dark:border-amber-700/60 bg-amber-50/50 dark:bg-amber-950/20 px-3.5 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* ======================================================== */}
        {/* TAB 3: BOTTOM GUIDANCE & DISCLAIMER                     */}
        {/* ======================================================== */}
        {activeTab === 'guidance' && (
          <div className="space-y-6">
            <div className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 shadow-card p-6 space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-gray-100 dark:border-dark-700">
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">
                    Bottom Guidance & Legal Disclaimer Card
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Edit the card title, intro explanation, bullet points checklist, and footer legal disclaimer shown at the bottom of the templates page.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (!window.confirm('Reset only the Bottom Guidance & Disclaimer text to default?')) return;
                    setForm((p) => ({
                      ...p,
                      bottomSection: {
                        title: 'A Practical Starting Point for Your Business',
                        intro: 'These free templates are intended to help BooksComply clients get started with commonly used business documentation.',
                        guidanceTitle: 'Before signing, sending, or relying on any document for a significant business or legal matter:',
                        checklist: [
                          'Customize the template to your specific circumstances.',
                          'Confirm that the provisions are appropriate for your business and jurisdiction.',
                          'Review applicable federal, state, and local requirements.',
                          'Consider having the final document reviewed by a qualified attorney when appropriate.',
                        ],
                        footerNotice: 'BooksComply provides these templates as a convenience to its clients and does not provide legal advice through these templates.',
                      },
                    }));
                    toastSuccess('Guidance card restored to defaults');
                  }}
                  className="px-3 py-1.5 rounded-xl text-xs font-semibold border border-gray-200 dark:border-dark-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 flex items-center gap-1.5"
                >
                  <FaUndo className="w-3 h-3" /> Reset This Card
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                    Card Title *
                  </label>
                  <input
                    type="text"
                    value={form.bottomSection?.title || ''}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        bottomSection: { ...p.bottomSection, title: e.target.value },
                      }))
                    }
                    placeholder="e.g. A Practical Starting Point for Your Business"
                    className="w-full text-sm rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3.5 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 font-semibold"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                    Intro Sentence *
                  </label>
                  <textarea
                    rows={2}
                    value={form.bottomSection?.intro || ''}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        bottomSection: { ...p.bottomSection, intro: e.target.value },
                      }))
                    }
                    placeholder="e.g. These free templates are intended to help BooksComply clients..."
                    className="w-full text-sm rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3.5 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                    Checklist Subheading *
                  </label>
                  <input
                    type="text"
                    value={form.bottomSection?.guidanceTitle || ''}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        bottomSection: { ...p.bottomSection, guidanceTitle: e.target.value },
                      }))
                    }
                    placeholder="e.g. Before signing, sending, or relying on any document..."
                    className="w-full text-sm rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3.5 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 font-medium"
                  />
                </div>

                {/* Checklist items */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400">
                      Checklist Bullet Points ({form.bottomSection?.checklist?.length || 0})
                    </label>
                    <button
                      type="button"
                      onClick={handleAddChecklistItem}
                      className="text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-primary-50 dark:bg-primary-900/30"
                    >
                      <FaPlus className="w-2.5 h-2.5" /> Add New Bullet Point
                    </button>
                  </div>

                  <div className="space-y-2.5">
                    {(form.bottomSection?.checklist || []).map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-xs font-bold flex items-center justify-center shrink-0">
                          {idx + 1}
                        </span>
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => handleUpdateChecklistItem(idx, e.target.value)}
                          className="flex-1 text-sm rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3.5 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            disabled={idx === 0}
                            onClick={() => handleMoveChecklistItem(idx, -1)}
                            title="Move Up"
                            className="p-2 text-gray-500 hover:text-gray-800 dark:hover:text-white disabled:opacity-30 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700"
                          >
                            <FaArrowUp className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            disabled={idx === (form.bottomSection?.checklist?.length || 0) - 1}
                            onClick={() => handleMoveChecklistItem(idx, 1)}
                            title="Move Down"
                            className="p-2 text-gray-500 hover:text-gray-800 dark:hover:text-white disabled:opacity-30 disabled:cursor-not-allowed rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700"
                          >
                            <FaArrowDown className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteChecklistItem(idx)}
                            title="Delete Point"
                            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg"
                          >
                            <FaTrash className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                    Footer Legal Notice (Bold bottom text) *
                  </label>
                  <textarea
                    rows={2}
                    value={form.bottomSection?.footerNotice || ''}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        bottomSection: { ...p.bottomSection, footerNotice: e.target.value },
                      }))
                    }
                    placeholder="e.g. BooksComply provides these templates as a convenience..."
                    className="w-full text-sm rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3.5 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 font-semibold"
                  />
                </div>
              </div>
            </div>

            {/* LIVE USER-SIDE PREVIEW CARD */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 shadow-card p-6 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-dark-700">
                <span className="text-xs font-bold uppercase tracking-wider text-primary-600 dark:text-primary-400 flex items-center gap-2">
                  <FaEye className="w-3.5 h-3.5" /> Live User-Side Preview
                </span>
                <span className="text-[11px] font-medium text-gray-400">
                  Updates in real time as you edit above
                </span>
              </div>

              {/* Exact Card from User Screenshot */}
              <div
                className="bg-white dark:bg-dark-850 rounded-2xl p-6 sm:p-8 space-y-4 border border-gray-200 dark:border-dark-700 shadow-sm"
                style={{ borderTop: '4px solid #3b82f6' }}
              >
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white tracking-tight">
                  {form.bottomSection?.title || 'A Practical Starting Point for Your Business'}
                </h2>

                <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 leading-relaxed font-normal">
                  {form.bottomSection?.intro ||
                    'These free templates are intended to help BooksComply clients get started with commonly used business documentation.'}
                </p>

                <p className="text-xs sm:text-sm font-medium text-gray-800 dark:text-gray-200 pt-1">
                  {form.bottomSection?.guidanceTitle ||
                    'Before signing, sending, or relying on any document for a significant business or legal matter:'}
                </p>

                <ul className="space-y-2 text-xs sm:text-sm text-gray-600 dark:text-gray-300 pl-1">
                  {(form.bottomSection?.checklist || []).map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="pt-3 border-t border-gray-100 dark:border-dark-700">
                  <p className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white">
                    {form.bottomSection?.footerNotice ||
                      'BooksComply provides these templates as a convenience to its clients and does not provide legal advice through these templates.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ======================================================== */}
      {/* MODAL: ADD / EDIT TEMPLATE                              */}
      {/* ======================================================== */}
      {templateModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/60 dark:bg-black/80 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 overflow-y-auto animate-fade-in"
            style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
            onClick={() => setTemplateModalOpen(false)}
          >
            <div
              className="bg-white dark:bg-dark-800 rounded-3xl border border-gray-100 dark:border-dark-700 shadow-2xl max-w-2xl w-full p-6 sm:p-7 space-y-5 my-8 relative z-10"
              onClick={(e) => e.stopPropagation()}
            >
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-dark-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 flex items-center justify-center">
                  <LuFileSpreadsheet className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">
                    {editingTemplate ? 'Edit Contract Template' : 'Add New Contract Template'}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Enter details and upload downloadable PDF / Word files.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setTemplateModalOpen(false)}
                className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-dark-700"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Category */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                  Category *
                </label>
                <select
                  value={templateForm.category}
                  onChange={(e) =>
                    setTemplateForm((p) => ({ ...p, category: e.target.value }))
                  }
                  className="w-full text-sm rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3.5 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {form.categories.map((c) => (
                    <option key={c.category} value={c.category}>
                      {c.category}
                    </option>
                  ))}
                  <option value="__NEW__">+ Create New Category...</option>
                </select>

                {templateForm.category === '__NEW__' && (
                  <input
                    type="text"
                    placeholder="Enter new category name..."
                    value={templateForm.newCategoryName}
                    onChange={(e) =>
                      setTemplateForm((p) => ({ ...p, newCategoryName: e.target.value }))
                    }
                    className="w-full mt-2 text-sm rounded-xl border border-primary-400 dark:border-primary-600 bg-primary-50/20 px-3.5 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                  Template Title *
                </label>
                <input
                  type="text"
                  placeholder="e.g. Non-Disclosure Agreement (NDA)"
                  value={templateForm.title}
                  onChange={(e) =>
                    setTemplateForm((p) => ({ ...p, title: e.target.value }))
                  }
                  className="w-full text-sm rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3.5 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                  Description *
                </label>
                <textarea
                  rows={3}
                  placeholder="Provide an overview of the template purpose and use cases..."
                  value={templateForm.description}
                  onChange={(e) =>
                    setTemplateForm((p) => ({ ...p, description: e.target.value }))
                  }
                  className="w-full text-sm rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3.5 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Optional Note */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                  Special Note (Optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. Note: This filing has specific IRS requirements..."
                  value={templateForm.note}
                  onChange={(e) =>
                    setTemplateForm((p) => ({ ...p, note: e.target.value }))
                  }
                  className="w-full text-sm rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3.5 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* File Uploads Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                {/* PDF File */}
                <div className="p-3.5 rounded-2xl border border-gray-200 dark:border-dark-600 bg-gray-50/50 dark:bg-dark-700/50 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                      <FaFilePdf className="w-4 h-4 text-red-500" /> PDF Document
                    </span>
                    {templateForm.pdfUrl && (
                      <button
                        type="button"
                        onClick={() =>
                          setTemplateForm((p) => ({ ...p, pdfUrl: '', pdfName: '' }))
                        }
                        className="text-xs text-red-500 hover:underline"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  {templateForm.pdfUrl ? (
                    <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 p-2 rounded-xl">
                      <FaCheckCircle className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{templateForm.pdfName || 'File uploaded'}</span>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 italic">No PDF file uploaded</div>
                  )}

                  <label className="btn-secondary w-full py-2 text-xs font-semibold flex items-center justify-center gap-1.5 cursor-pointer">
                    <FaUpload className="w-3 h-3" />
                    {uploadingPdf ? 'Uploading...' : templateForm.pdfUrl ? 'Replace PDF' : 'Upload PDF'}
                    <input
                      type="file"
                      accept=".pdf"
                      disabled={uploadingPdf}
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setUploadingPdf(true);
                        try {
                          const res = await handleUploadFile(file, 'pdf');
                          setTemplateForm((p) => ({
                            ...p,
                            pdfUrl: res.fileUrl,
                            pdfName: res.originalName,
                          }));
                          toastSuccess('PDF uploaded');
                        } catch (err) {
                          toastError(err.message || 'Upload failed');
                        } finally {
                          setUploadingPdf(false);
                          e.target.value = '';
                        }
                      }}
                    />
                  </label>
                </div>

                {/* DOC File */}
                <div className="p-3.5 rounded-2xl border border-gray-200 dark:border-dark-600 bg-gray-50/50 dark:bg-dark-700/50 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                      <FaFileWord className="w-4 h-4 text-blue-600" /> Word / DOC File
                    </span>
                    {templateForm.docUrl && (
                      <button
                        type="button"
                        onClick={() =>
                          setTemplateForm((p) => ({ ...p, docUrl: '', docName: '' }))
                        }
                        className="text-xs text-red-500 hover:underline"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  {templateForm.docUrl ? (
                    <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 p-2 rounded-xl">
                      <FaCheckCircle className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{templateForm.docName || 'File uploaded'}</span>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 italic">No Word file uploaded</div>
                  )}

                  <label className="btn-secondary w-full py-2 text-xs font-semibold flex items-center justify-center gap-1.5 cursor-pointer">
                    <FaUpload className="w-3 h-3" />
                    {uploadingDoc ? 'Uploading...' : templateForm.docUrl ? 'Replace DOC' : 'Upload Word / DOC'}
                    <input
                      type="file"
                      accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      disabled={uploadingDoc}
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setUploadingDoc(true);
                        try {
                          const res = await handleUploadFile(file, 'doc');
                          setTemplateForm((p) => ({
                            ...p,
                            docUrl: res.fileUrl,
                            docName: res.originalName,
                          }));
                          toastSuccess('DOC file uploaded');
                        } catch (err) {
                          toastError(err.message || 'Upload failed');
                        } finally {
                          setUploadingDoc(false);
                          e.target.value = '';
                        }
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100 dark:border-dark-700">
              <button
                type="button"
                onClick={() => setTemplateModalOpen(false)}
                className="px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-dark-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveTemplateModal}
                className="btn-premium px-5 py-2 text-sm font-semibold flex items-center gap-2"
              >
                <FaCheckCircle className="w-3.5 h-3.5" />
                {editingTemplate ? 'Update Template' : 'Add Template'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ======================================================== */}
      {/* MODAL: ADD / RENAME CATEGORY                            */}
      {/* ======================================================== */}
      {categoryModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/60 dark:bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in"
            style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
            onClick={() => setCategoryModalOpen(false)}
          >
            <div
              className="bg-white dark:bg-dark-800 rounded-3xl border border-gray-100 dark:border-dark-700 shadow-2xl max-w-md w-full p-6 space-y-4 relative z-10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between pb-3 border-b border-gray-100 dark:border-dark-700">
                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                  {editingCategoryIndex !== null ? 'Rename Category' : 'Create New Category'}
                </h3>
                <button
                  type="button"
                  onClick={() => setCategoryModalOpen(false)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <FaTimes className="w-4 h-4" />
                </button>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                  Category Name *
                </label>
                <input
                  type="text"
                  placeholder="e.g. Intellectual Property Agreements"
                  value={categoryNameInput}
                  onChange={(e) => setCategoryNameInput(e.target.value)}
                  autoFocus
                  className="w-full text-sm rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 px-3.5 py-2.5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setCategoryModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-dark-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveCategoryModal}
                  className="btn-premium px-4 py-2 text-sm font-semibold"
                >
                  {editingCategoryIndex !== null ? 'Save Name' : 'Create Category'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </PageLayout>
  );
};

export default ContractTemplatesPortalCMS;
